import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { WarningOutlined } from '@ant-design/icons'
import { Button, Divider, Row, Typography } from 'antd'
import { includes, isEmpty, isNil, pick } from 'lodash'

import { GridFormItem } from '../../../core/components/sections'

import { USER_ASSIGNMENT_ACTION_TYPE } from '../../../common/constants'

import { MENU_CURATION_TASK_STATUS } from '../../constants'
import {
  useMenuCurationTaskAllocatedUsers,
  useMenuCurationTaskAssignUserDisabledFlags,
  useRemoveFromAllocationMutation,
} from '../../hooks'
import { menuCurationTaskPropType } from '../../prop-types'
import { StyledAllocatedUserLabel, StyledMenuAvailabiltyLabel } from '../styled'

import MenuCurationTaskHistoryModal from './menu-curation-task-history-modal'
import MenuCurationTaskTimestamps from './menu-curation-task-timestamps'
import MenuCurationTaskUserAssignment from './menu-curation-task-user-assignment'

const MenuCurationTaskDetailsAssignation = ({ menuCurationTask }) => {
  const {
    id,
    assignedTierOneCurator,
    assignedCurator,
    assignedQa,
    assignedQc,
    assignedUnknownWordsCurator,
    assignedUnknownWordsQa,
    assignedScrapingReviewerUser,
    isAvailableForTierOne,
    isAvailableForUnknownWordsCuration,
    isAvailableForUnknownWordsQa,
    isAvailableForCuration,
    isAvailableForQa,
    isAvailableForQc,
    taskStatus,
  } = menuCurationTask

  const { isUpdateLoading } = useSelector(
    store => store.menuCurationTasks.menuCurationTask,
  )

  const menuCurationTaskTimestamps = useMemo(
    () => ({
      ...pick(menuCurationTask, [
        'tierOneDoneAt',
        'tierOneDoneBy',
        'curatedAt',
        'qadAt',
        'qcdAt',
        'curatedBy',
        'qadBy',
        'qcdBy',
        'tierOneCuratorAssignedAt',
        'unknownWordsCuratorAssignedAt',
        'unknownWordsQaAssignedAt',
        'curatorAssignedAt',
        'lastManualUpdateAt',
        'qaAssignedAt',
        'qcAssignedAt',
        'unknownWordsCuratedAt',
        'unknownWordsQadAt',
        'unknownWordsCuratedBy',
        'lastManualUpdateBy',
        'unknownWordsQadBy',
        'latestTierOneDoneDishCount',
        'latestCuratedDishCount',
        'latestQadDishCount',
        'latestQcdDishCount',
        'latestCuratedUnknownWordCount',
        'latestQadUnknownWordCount',
      ]),
      ...pick(menuCurationTask.menu, [
        'autoeatUpdatedAt',
        'autoeatUpdateAppliedAt',
      ]),
    }),
    [menuCurationTask],
  )

  const {
    isUnknownWordsCurationAssignationDisabled,
    isUnknownWordsQaAssignationDisabled,
    isTierOneAssignationDisabled,
    isCuratorAssignationDisabled,
    isQaAssignationDisabled,
    isQcAssignationDisabled,
    isScrapingReviewUserAssignationDisabled,
  } = useMenuCurationTaskAssignUserDisabledFlags(menuCurationTask)

  const {
    isCurrentUserAllocated,
    allocatedCurator,
    allocatedQa,
    allocatedQc,
    allocatedTierOneCurator,
  } = useMenuCurationTaskAllocatedUsers(menuCurationTask)

  const [removeFromAllocation] = useRemoveFromAllocationMutation(
    menuCurationTask.id,
  )

  const handleRemoveFromAllocation = useCallback(() => {
    removeFromAllocation()
  }, [removeFromAllocation])

  const alreadyAssignedUsersDuringCuration = useMemo(
    () => [assignedQc?.id],
    [assignedQc],
  )

  const alreadyAssignedUsersDuringQa = useMemo(
    () => [assignedQc?.id],
    [assignedQc],
  )

  const alreadyAssignedUsersDuringQc = useMemo(
    () => [assignedCurator?.id, assignedQa?.id],
    [assignedCurator, assignedQa],
  )

  const isScrapingReviewPending = useMemo(
    () =>
      includes(
        [
          MENU_CURATION_TASK_STATUS.SCRAPING_REVIEW_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.AWAITING_SCRAPING_REVIEW.value,
          MENU_CURATION_TASK_STATUS.AWAITING_AUTOEAT_UPDATES.value,
          MENU_CURATION_TASK_STATUS.NEEDS_SCRAPING_CLARIFICATION.value,
        ],
        taskStatus,
      ),
    [taskStatus],
  )

  const isAllocatedCuratorLabelVisible = useMemo(
    () =>
      !isEmpty(allocatedCurator) &&
      !isCurrentUserAllocated &&
      isNil(assignedCurator),
    [allocatedCurator, assignedCurator, isCurrentUserAllocated],
  )

  const isAllocatedTierOneLabelVisible = useMemo(
    () =>
      !isEmpty(allocatedTierOneCurator) &&
      !isCurrentUserAllocated &&
      isNil(assignedTierOneCurator),
    [allocatedTierOneCurator, assignedTierOneCurator, isCurrentUserAllocated],
  )

  const isAllocatedQaLabelVisible = useMemo(
    () => !isEmpty(allocatedQa) && !isCurrentUserAllocated && isNil(assignedQa),
    [allocatedQa, assignedQa, isCurrentUserAllocated],
  )

  const isAllocatedQcLabelVisible = useMemo(
    () => !isEmpty(allocatedQc) && !isCurrentUserAllocated && isNil(assignedQc),
    [allocatedQc, assignedQc, isCurrentUserAllocated],
  )

  return (
    <>
      <Row gutter={16}>
        <GridFormItem span={12} label="Timestamps">
          <MenuCurationTaskTimestamps timestamps={menuCurationTaskTimestamps} />
          <MenuCurationTaskHistoryModal menuCurationTaskId={id} />
        </GridFormItem>
        <GridFormItem span={4} label="Status">
          <Typography.Text strong>
            {MENU_CURATION_TASK_STATUS[menuCurationTask.taskStatus]?.label}
          </Typography.Text>
          <br />
          {menuCurationTask.menu?.hasPendingAutoeatUpdates && (
            <Typography.Text type="warning">
              <WarningOutlined />
              This menu has pending Autoeat updates. They will be applied after
              current step is completed.
            </Typography.Text>
          )}
        </GridFormItem>
        <GridFormItem span={4} label="Availabilty">
          <>
            {isAvailableForTierOne && (
              <StyledMenuAvailabiltyLabel>Tier 1</StyledMenuAvailabiltyLabel>
            )}
            {isAvailableForUnknownWordsCuration && (
              <StyledMenuAvailabiltyLabel>
                UW Curation
              </StyledMenuAvailabiltyLabel>
            )}
            {isAvailableForUnknownWordsQa && (
              <StyledMenuAvailabiltyLabel>UW QA</StyledMenuAvailabiltyLabel>
            )}
            {isAvailableForCuration && (
              <StyledMenuAvailabiltyLabel>Curation</StyledMenuAvailabiltyLabel>
            )}
            {isAvailableForQa && (
              <StyledMenuAvailabiltyLabel>QA</StyledMenuAvailabiltyLabel>
            )}
            {isAvailableForQc && (
              <StyledMenuAvailabiltyLabel>QC</StyledMenuAvailabiltyLabel>
            )}
          </>
        </GridFormItem>
      </Row>
      <Divider />
      <Row gutter={16}>
        {isScrapingReviewPending && (
          <GridFormItem span={4} label="Scraping Reviewer">
            <MenuCurationTaskUserAssignment
              menuCurationTaskId={id}
              userActionType={USER_ASSIGNMENT_ACTION_TYPE.SCRAPING_REVIEW}
              assignedUser={assignedScrapingReviewerUser}
              otherAssignedUsers={[]}
              isDisabled={
                isUpdateLoading || isScrapingReviewUserAssignationDisabled
              }
              unassignedLabel="Assign to me"
            />
          </GridFormItem>
        )}
        {!isScrapingReviewPending && (
          <>
            <GridFormItem span={4} label="Tier One Curator">
              {isAllocatedTierOneLabelVisible ? (
                <StyledAllocatedUserLabel type="secondary">
                  Allocated Tier One Curator: {allocatedTierOneCurator.name}
                </StyledAllocatedUserLabel>
              ) : (
                <MenuCurationTaskUserAssignment
                  menuCurationTaskId={id}
                  userActionType={USER_ASSIGNMENT_ACTION_TYPE.TIER_ONE}
                  assignedUser={assignedTierOneCurator}
                  otherAssignedUsers={[]}
                  isDisabled={isUpdateLoading || isTierOneAssignationDisabled}
                  unassignedLabel="Assign to me"
                />
              )}
            </GridFormItem>
            <GridFormItem span={4} label="U.W. Curator">
              {isAllocatedCuratorLabelVisible ? (
                <StyledAllocatedUserLabel type="secondary">
                  Allocated curator: {allocatedCurator.name}
                </StyledAllocatedUserLabel>
              ) : (
                <MenuCurationTaskUserAssignment
                  menuCurationTaskId={id}
                  userActionType={
                    USER_ASSIGNMENT_ACTION_TYPE.UNKNOWN_WORDS_CURATION
                  }
                  assignedUser={assignedUnknownWordsCurator}
                  otherAssignedUsers={[]}
                  isDisabled={
                    isUpdateLoading || isUnknownWordsCurationAssignationDisabled
                  }
                  unassignedLabel="Assign to me"
                />
              )}
            </GridFormItem>

            <GridFormItem span={4} label="U.W. Qa">
              {isAllocatedQaLabelVisible ? (
                <StyledAllocatedUserLabel type="secondary">
                  Allocated QA: {allocatedQa.name}
                </StyledAllocatedUserLabel>
              ) : (
                <MenuCurationTaskUserAssignment
                  menuCurationTaskId={id}
                  userActionType={USER_ASSIGNMENT_ACTION_TYPE.UNKNOWN_WORDS_QA}
                  assignedUser={assignedUnknownWordsQa}
                  otherAssignedUsers={[]}
                  isDisabled={
                    isUpdateLoading || isUnknownWordsQaAssignationDisabled
                  }
                  unassignedLabel="Assign to me"
                />
              )}
            </GridFormItem>

            <GridFormItem span={4} label="Curator">
              {isAllocatedCuratorLabelVisible ? (
                <StyledAllocatedUserLabel type="secondary">
                  Allocated curator: {allocatedCurator.name}
                </StyledAllocatedUserLabel>
              ) : (
                <MenuCurationTaskUserAssignment
                  menuCurationTaskId={id}
                  userActionType={USER_ASSIGNMENT_ACTION_TYPE.CURATION}
                  assignedUser={assignedCurator}
                  otherAssignedUsers={alreadyAssignedUsersDuringCuration}
                  isDisabled={isUpdateLoading || isCuratorAssignationDisabled}
                  unassignedLabel="Assign to me"
                />
              )}
            </GridFormItem>

            <GridFormItem span={4} label="Qa">
              {isAllocatedQaLabelVisible ? (
                <StyledAllocatedUserLabel type="secondary">
                  Allocated QA: {allocatedQa.name}
                </StyledAllocatedUserLabel>
              ) : (
                <MenuCurationTaskUserAssignment
                  menuCurationTaskId={id}
                  userActionType={USER_ASSIGNMENT_ACTION_TYPE.QA}
                  assignedUser={assignedQa}
                  otherAssignedUsers={alreadyAssignedUsersDuringQa}
                  isDisabled={isUpdateLoading || isQaAssignationDisabled}
                  unassignedLabel="Assign to me"
                />
              )}
            </GridFormItem>

            <GridFormItem span={4} label="Qc">
              {isAllocatedQcLabelVisible ? (
                <StyledAllocatedUserLabel type="secondary">
                  Allocated QC: {allocatedQc.name}
                </StyledAllocatedUserLabel>
              ) : (
                <MenuCurationTaskUserAssignment
                  menuCurationTaskId={id}
                  userActionType={USER_ASSIGNMENT_ACTION_TYPE.QC}
                  assignedUser={assignedQc}
                  otherAssignedUsers={alreadyAssignedUsersDuringQc}
                  isDisabled={isUpdateLoading || isQcAssignationDisabled}
                  unassignedLabel="Assign to me"
                />
              )}
            </GridFormItem>
          </>
        )}
      </Row>

      {isCurrentUserAllocated && (
        <Row gutter={16}>
          <Button type="link" onClick={handleRemoveFromAllocation}>
            Remove from allocation
          </Button>
        </Row>
      )}
      <Divider />
    </>
  )
}

MenuCurationTaskDetailsAssignation.propTypes = {
  menuCurationTask: menuCurationTaskPropType.isRequired,
}

export default MenuCurationTaskDetailsAssignation
